import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * v3 note: v2's FormControl/FormLabel/FormErrorMessage/FormHelperText merge
 * into a single `Field` compound (Field.Root/Label/ErrorText/HelperText/
 * RequiredIndicator), whose recipe is the built-in `field` slot recipe. v2
 * only customized FormLabel, but v3's built-in field recipe is not a neutral
 * stand-in for v2's stock Form styles, so every slot restores v2 here:
 *
 * - `root`: v2's FormControl was a plain block (`width: 100%`,
 *   `position: relative`). The built-in default (`vertical`) orientation
 *   makes it a `gap: 1.5` flex column with `alignItems: flex-start`, which
 *   shrank every child without `w="100%"` to its content, collapsed
 *   MFormField's space-between label row, and added 6px under the label.
 *   `flexDirection`/`alignItems`/`gap` are restated too, so a call-site
 *   `display="flex"` (AnchorBillingDay) gets v2's row again rather than the
 *   column. `horizontal` stays on the built-in (flex row, centered), which
 *   is what its call sites (SettingsControl etc.) want.
 * - `label`: the app's v2 FormLabel theme plus v2's own `display: block`
 *   (the built-in is an inline-flex row with a `gap`). `mb: 1` is the 4px
 *   label → input gap.
 * - `errorText`/`helperText`/`requiredIndicator`: v2's stock spacing and
 *   sizes (`mt: 2`, `fontSize: sm`, `lineHeight: normal`; indicator
 *   `marginStart: 1`), replacing v3's 12px medium-weight text that relied on
 *   the root's gap for spacing.
 */

export const fieldTheme = defineSlotRecipe({
  slots: [
    'root',
    'label',
    'input',
    'textarea',
    'errorText',
    'helperText',
    'select',
    'requiredIndicator',
  ],
  base: {
    label: {
      display: 'block',
      fontSize: 'sm', // 13px
      lineHeight: 'inherit',
      marginEnd: 3,
      mb: 1,
      fontWeight: 500,
      color: 'fg.strong',
    },
    errorText: {
      display: 'flex',
      mt: 2,
      fontSize: 'sm',
      lineHeight: 'normal',
      fontWeight: 'inherit',
    },
    helperText: {
      // Ark's HelperText is a `<span>`; v2's FormHelperText was a block `div`,
      // so without this it sits inline and loses its top gap.
      display: 'block',
      mt: 2,
      fontSize: 'sm',
      lineHeight: 'normal',
    },
    requiredIndicator: {
      marginStart: 1,
    },
  },
  variants: {
    orientation: {
      vertical: {
        root: {
          display: 'block',
          flexDirection: 'row',
          alignItems: 'normal',
          gap: 0,
        },
      },
    },
  },
});

export default fieldTheme;
